<template>
    <div>
        <div class="charts-container">
            <CustomerPieChart v-show="!loading" :valueList="costList"></CustomerPieChart>
            <b-skeleton type="avatar" v-if="loading" class="skeleton-size"></b-skeleton>
        </div>

        <b-table
            :fields="fields"
            :items="items"
            :class="['gk-table gk-table--responsive gk-table--striped gk-table--headed']"
            :busy="loading"
            :tbody-tr-class="rowClass"
            show-empty
            table-busy>
            <template #cell(name)="row">
                <span>{{ row.value }}</span>
                &nbsp;
                <font-awesome-icon v-if="row.item.is_archived" icon="fa-solid fa-folder" />
            </template>
            <template #head(name)>
                <translate>Supplier</translate>
                &nbsp;
                <GekkoButton
                    @click="sortByName"
                    :icon="sortNameDesc ? 'fa fa-arrow-down-z-a' : 'fa fa-arrow-up-z-a'"
                    class="p-0 sort-icon-class"
                    variant="link"
                />
            </template>
            <template #cell(unpaid)="row">{{ formatNumber(row.value) }}</template>
            <template #cell(paid)="row">{{ formatNumber(row.value) }}</template>
            <template #cell(net)="row">{{ formatNumber(row.value) }}</template>
        </b-table>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import CostService from "@/store/services/cost.service.js"
import CustomerPieChart from '@/components/customer_insight/CustomerPieChart'
import GekkoButton from '@/components/GekkoButton'

export default {
    name: "SupplierCostsTable",
    components: {CustomerPieChart, GekkoButton},
    props: {
        customers: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            items: [],
            fields: [
                { key: 'name', label: this.$gettext('Supplier')},
                { key: 'unpaid', label: this.$gettext('Unpaid'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'paid', label: this.$gettext('Paid'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'net', label: this.$gettext('NET'), thClass: 'th-class', tdClass: 'td-class', sortable: true}
            ],
            sortNameDesc: false,
            loading_customers: true,
            loading_filters: true
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
        costList() {
            return this.items.filter((item) => item.net > 0).map((item) => { return {'label': item.name, 'value': item.net}})
        },
        loading() {
            return this.loading_customers || this.loading_filters
        }
    },
    watch: {
        customers() {
            this.loading_customers = true
            this.setReportData('customers')
        },
        filters() {
            this.loading_filters = true
            this.setReportData('filters')
        }
    },
    methods: {
        setReportData(loading_type) {
            if (this.$store.getters['getIsPremium']) {
                if (this.customers.length)
                    this.setRealData(loading_type)
            } else
                this.setFakeData(loading_type)
        },
        setRealData(loading_type) {
            let temp_data = JSON.parse(JSON.stringify(this.customers))
            temp_data.unshift({'id': null, 'name': this.$gettext('(no customer)')})

            let filters_costs = {
                'start_date': this.filters.start_date,
                'end_date': this.filters.end_date,
                'group_by': 'supplier',
                'owner_company': this.getCompanyId,
                'tax_exists': true,
                'tax_exempt': true,
                'btw_reversed_charge': true
            }
            
            CostService.totalNet(filters_costs).then((response) => {
                this.items = []
                temp_data = this.mergeTwoLists(temp_data, response.data, 'id', 'supplier', 'total_expenses', 'total_net')
                temp_data = this.mergeTwoLists(temp_data, response.data, 'id', 'supplier', 'unpaid', 'total_unpaid')
                
                for(let i = 0; i<temp_data.length; i++) {
                    if(isNaN(temp_data[i].total_expenses) || temp_data[i].total_expenses == null)
                        temp_data[i].total_expenses = 0
                    
                    if (temp_data[i].total_expenses != 0) {
                        this.items.push({
                            'id': temp_data[i].id,
                            'name' : temp_data[i].name,
                            'net': temp_data[i].total_expenses,
                            'unpaid': temp_data[i].unpaid,
                            'paid': temp_data[i].total_expenses - temp_data[i].unpaid,
                            'is_archived': temp_data[i].is_archived
                        })
                    }
                }
                
                this.loading_customers = false
                this.loading_filters = false
            });
        },
        setFakeData(loading_type) {
            this.items = []
            let i = 1

            for(i = 1; i <= 4; i++) {
                this.items.push({
                    'id': i,
                    'name' : `${this.$gettext("fake customer")} ${i}`,
                    'net': 200 * i,
                    'unpaid': 10 * i,
                    'paid': 200 * i - 10 * i,
                    'is_archived': false
                })
            }
            i++
            this.items.push({
                'id': i,
                'name' : `${this.$gettext("fake customer")} ${i}`,
                'net': 200 * i,
                'unpaid': 10 * i,
                'paid': 200 * i - 10 * i,
                'is_archived': true
            })

            if (loading_type === 'customers')
                this.loading_customers = false
            if (loading_type === 'filters')
                this.loading_filters = false
        },
        mergeTwoLists(list1, list2, ifProp1, ifProp2, prop1, prop2) {
            let finalList = list1.map((x) => x)
            for(let i in finalList) {
                for (let j in list2) {
                    if (finalList[i][ifProp1] == list2[j][ifProp2]) {
                        finalList[i][prop1] = list2[j][prop2]
                    }
                }
            }
            return finalList
        },
        formatNumber(num) {
            num = parseInt(num.toFixed(0))
            let str = num.toLocaleString('en-US')
            return `€ ${str}`
        },
        sortByName() {
            if (this.sortNameDesc) {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase()
                    const nameB = b.name.toUpperCase()

                    if (nameA < nameB) {
                        return -1
                    }

                    if (nameA > nameB) {
                        return 1
                    }

                    return 0
                })
            } else {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase();
                    const nameB = b.name.toUpperCase();

                    if (nameA > nameB) {
                        return -1
                    }

                    if (nameA < nameB) {
                        return 1
                    }

                    return 0
                })
            }

            this.sortNameDesc = !this.sortNameDesc
        },
        rowClass(item) {
            if (item && item.is_archived)
                return "archived-row"
            return ""
        }
    }
}
</script>

<style scoped>
.charts-container {
    padding-top: 1rem;
    padding-bottom: 1rem;
    display: flex;
    width: 100%;
    justify-content: center;
    overflow: auto;
}

.skeleton-size {
    min-width: 180px;
    width: 450px;
    height: 450px;
}
</style>

<style>
.th-class {
    text-align: right;
}

.td-class {
    text-align: right;
}

.sort-icon-class:hover {
    color: #3f444d;
}

.archived-row {
    color: #bbb;
}
</style>